/**
 * The numbers the console could never show without adding up a list by eye.
 *
 * All of it is derived from the client document that is already loaded — no
 * resolver, no extra round trip. Kept here rather than inline in SingleClient
 * for the same reason `clientSectionSelectors.js` exists: "what counts as
 * outstanding" living in two places is a drift bug with a date on it.
 *
 * MONEY IS CENTS. Every amount in this app is stored as a whole-number STRING
 * of cents, so these sum integers and hand back a cents string for the caller
 * to run through `priceStrFormatter`. A float anywhere in that chain is how
 * $1,234.56 becomes $1,234.5600000001 (CLAUDE.md, money handling).
 */

const centsOf = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
};

const msOf = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * @returns {{
 *   lifetimeCents: string, outstandingCents: string, unpaidCount: number,
 *   lastJobMs: number|null, nextJobMs: number|null,
 *   quotesTotal: number, quotesWon: number, winRate: number|null
 * }}
 */
export const clientSummary = (clientObj) => {
  const invoices = Array.isArray(clientObj?.invoices) ? clientObj.invoices : [];
  const quotes = Array.isArray(clientObj?.quotes) ? clientObj.quotes : [];

  let lifetime = 0;
  let outstanding = 0;
  let unpaidCount = 0;
  let lastJob = null;
  let nextJob = null;

  /*
   * "Now" is captured once rather than per iteration, so a list cannot be
   * split across a tick and classify two jobs on the same timestamp
   * differently.
   */
  const now = Date.now();

  invoices.forEach((invoice) => {
    const cents = centsOf(invoice?.invoiceTotal);

    if (invoice?.invoicePaid) {
      lifetime += cents;
    } else {
      outstanding += cents;
      unpaidCount += 1;
    }

    /*
     * jobScheduledFor before dateCreated — on an imported job the scheduled
     * date is the work, while dateCreated can be the date of the import run.
     * The same rule the rail's "Since" line and the Activity feed follow.
     */
    const at = msOf(invoice?.jobScheduledFor) || msOf(invoice?.dateCreated);
    if (at === null) return;

    if (at <= now) {
      if (lastJob === null || at > lastJob) lastJob = at;
    } else if (nextJob === null || at < nextJob) {
      // The NEAREST upcoming job, not the furthest out.
      nextJob = at;
    }
  });

  /*
   * Quote conversion. `/invoiced/gi` is what ClientQuotesDisplay's bullet
   * already treats as won — change one and change the other, or a quote reads
   * green in the list and lost in the summary.
   *
   * Active quotes are excluded from the denominator: a live quote has not
   * been lost yet, and counting it as a loss makes the rate drop every time
   * someone sends one.
   */
  let decided = 0;
  let won = 0;
  quotes.forEach((quote) => {
    const status = quote?.quoteStatus || "";
    if (/active/gi.test(status)) return;
    decided += 1;
    if (/invoiced/gi.test(status)) won += 1;
  });

  return {
    lifetimeCents: String(lifetime),
    outstandingCents: String(outstanding),
    unpaidCount,
    lastJobMs: lastJob,
    nextJobMs: nextJob,
    quotesTotal: decided,
    quotesWon: won,
    // null, not 0, when nothing has been decided — "0%" would be a verdict on
    // a client who simply has no history yet.
    winRate: decided > 0 ? Math.round((won / decided) * 100) : null,
  };
};

export default clientSummary;
